import style from "./TimeTable.module.css";
import ClassNames from "../../util/ClassNames";
import { Availability, Course, Store } from "../../api/ApiData";
import Time from "../../util/Time";
import Settings from "../../util/Settings";
import { Box, Stack, SvgIcon, SvgIconProps } from "@mui/material";
import { ColorHexCodes } from "../../util/Color";
import { getStoreBusinessHours, getSlotDuration, createDaysLabelItem } from "./TimeTableUtils";

type TimeTableArgs = {
  page: number;
  store: Store;
  availability: Availability;
  onPaging: (page: number) => void;
  onSelected: (unixTime: number) => void;
};

export default function TimeTable({ page, store, availability, onPaging, onSelected }: TimeTableArgs) {
  const businessHours = getStoreBusinessHours(store);
  const slotDuration = getSlotDuration(store);

  const slots: number[] = [];
  for (let slot = businessHours.openAt; slot < businessHours.closeAt; slot += slotDuration) {
    slots.push(slot);
  }

  // 列のラベル(日付)を作成する
  const daysLabelItem = createDaysLabelItem(page);

  // 行のラベル(時間)を作成する
  const timeLabels = createTimeLabels(slots);

  // 予約セルを作成する
  const dataCells = createDataCells(slots, availability, page, onSelected);

  return (
    <Stack sx={{ textAlign: "center" }} className={ClassNames(style.cellWrapper)}>
      <Stack direction={"row"} className={ClassNames(style.cell)}>
        <Stack
          direction={"row"}
          sx={{ color: page > 0 ? "black" : "gray", p: "10px" }}
          className={page > 0 ? ClassNames(style.clickable) : ""}
          onClick={() => {
            page > 0 && onPaging(page - 1);
          }}
        >
          <TriangleArrowIcon arrowDirection={"left"} />
          前の週
        </Stack>
        <Stack className={ClassNames(style.grow)} />
        <Stack direction={"row"} sx={{ p: "10px" }} className={ClassNames(style.clickable)} onClick={() => onPaging(page + 1)}>
          次の週
          <TriangleArrowIcon />
        </Stack>
      </Stack>
      <Stack direction={"row"} position="sticky" sx={{ top: 0 }} bgcolor="white">
        <Box className={ClassNames(style.grow, style.cell, style.timeColumn)}>日時</Box>
        {daysLabelItem}
      </Stack>
      <Stack direction={"row"}>
        <Stack className={ClassNames(style.grow, style.timeColumn)}>{timeLabels}</Stack>
        {dataCells}
      </Stack>
    </Stack>
  );
}

function createTimeLabels(slots: number[]) {
  return slots.map((slot, i) => {
    const totalMinutes = slot / Time.AMinute;
    const hour = Math.floor(totalMinutes / 60);
    const min = (totalMinutes % 60).toString().padStart(2, "0");
    return (
      <Box key={i} className={ClassNames(style.cell, style.timeColumn)} style={{ height: "45px" }}>
        {`${hour}:${min}`}
      </Box>
    );
  });
}

function createDataCells(slots: number[], availability: Availability, page: number, onSelected: (unixTime: number) => void) {
  return Array(Settings.DateUnitCount)
    .fill(0)
    .map((_, i) => {
      const date = Time.Now().Today() + (i + page * Settings.DateUnitCount) * Time.ADay;
      const column = slots.map((slot) => {
        if (availability.includes(date + slot)) {
          return (
            <Box key={slot} style={{ color: `#${ColorHexCodes.RequiredRed}` }} className={ClassNames(style.cell, style.clickable, style.availablityCell)} onClick={() => onSelected(date + slot)}>
              ◎
            </Box>
          );
        } else {
          return (
            <Box key={slot} className={ClassNames(style.cell, style.unavailable, style.availablityCell)}>
              ×
            </Box>
          );
        }
      });
      return (
        <Stack key={i} className={style.grow}>
          {column}
        </Stack>
      );
    });
}

interface TriangleArrowIconProps extends SvgIconProps {
  arrowDirection?: "left" | "right";
}
const TriangleArrowIcon: React.FC<TriangleArrowIconProps> = ({ arrowDirection = "right", ...props }) => {
  const points = arrowDirection === "left" ? "15,5 9,10 15,15" : "5,5 11,10 5,15";
  return (
    <SvgIcon {...props}>
      <polygon points={points} />
    </SvgIcon>
  );
};
